<template>
  <div v-redirect-if-no-permission:VIEW_FINANCE_REPORTS class="fin-page">
    <finance-section-nav />
    <loading-component :value="loading" />

    <v-dialog v-model="formDialog" max-width="820px" persistent>
      <v-card>
        <v-card-title>
          {{ isEditing ? $t("finance.employees.editEmployee") : $t("finance.employees.newEmployee") }}
          <v-spacer></v-spacer>
          <v-btn icon @click="closeForm"><v-icon>close</v-icon></v-btn>
        </v-card-title>
        <v-card-text>
          <v-layout wrap>
            <v-flex xs12 md6 class="pa-2">
              <v-text-field v-model="form.full_name" :label="$t('finance.employees.fullName')"></v-text-field>
            </v-flex>
            <v-flex xs12 md6 class="pa-2">
              <v-text-field v-model="form.employee_no" :label="$t('finance.employees.employeeNo')"></v-text-field>
            </v-flex>
            <v-flex xs12 md6 class="pa-2">
              <v-combobox
                v-model="form.department"
                :items="departments"
                :label="$t('finance.employees.department')"
              ></v-combobox>
            </v-flex>
            <v-flex xs12 md6 class="pa-2">
              <v-text-field v-model="form.job_title" :label="$t('finance.employees.jobTitle')"></v-text-field>
            </v-flex>
            <v-flex xs12 md6 class="pa-2">
              <v-text-field v-model="form.phone_number" :label="$t('finance.employees.phone')"></v-text-field>
            </v-flex>
            <v-flex xs12 md6 class="pa-2">
              <v-text-field
                v-model.number="form.basic_salary"
                type="number"
                min="0"
                :label="$t('finance.employees.basicSalary')"
                :hint="$t('finance.employees.basicSalaryHint')"
                persistent-hint
              ></v-text-field>
            </v-flex>
            <v-flex xs12 md6 class="pa-2">
              <v-text-field v-model="form.hired_at" type="date" :label="$t('finance.employees.hiredAt')"></v-text-field>
            </v-flex>
            <v-flex xs12 md6 class="pa-2">
              <v-text-field
                v-model="form.terminated_at"
                type="date"
                :label="$t('finance.employees.terminatedAt')"
              ></v-text-field>
            </v-flex>
            <v-flex xs12 md6 class="pa-2">
              <v-switch v-model="form.is_active" :label="$t('finance.employees.active')"></v-switch>
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="closeForm">{{ $t("finance.common.cancel") }}</v-btn>
          <v-btn color="primary" :loading="saving" @click="submitForm">{{ $t("finance.common.apply") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="560px" persistent>
      <v-card>
        <v-card-title class="error--text">{{ $t("finance.employees.deleteTitle") }}</v-card-title>
        <v-card-text>
          <div class="emp-notice emp-notice--warn">{{ $t("finance.employees.deleteWarning") }}</div>
          <div v-if="deleteTarget">{{ deleteTarget.full_name }}</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="deleteDialog = false">{{ $t("finance.common.cancel") }}</v-btn>
          <v-btn color="error" :loading="deleting" @click="submitDelete">
            {{ $t("finance.employees.deleteConfirm") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-layout wrap>
      <v-flex xs12 md5 class="pr-1">
        <v-card>
          <div class="fin-header">
            <div class="fin-toolbar">
              <span class="fin-title">{{ $t("finance.employees.title") }}</span>
              <v-spacer></v-spacer>
              <v-btn v-if="canWrite" small color="primary" @click="openCreate">
                <v-icon small left>add</v-icon>
                {{ $t("finance.employees.addEmployee") }}
              </v-btn>
              <v-tooltip bottom>
                <v-btn slot="activator" icon small flat color="primary" @click="loadEmployees">
                  <v-icon small>refresh</v-icon>
                </v-btn>
                <span>{{ $t("finance.common.refresh") }}</span>
              </v-tooltip>
            </div>
            <div class="fin-filters">
              <v-text-field
                v-model="filters.search"
                :label="$t('finance.common.search')"
                hide-details
                clearable
                class="fin-field--wide"
                @keyup.enter="loadEmployees"
              ></v-text-field>
              <v-select
                v-model="filters.department"
                :items="departmentOptions"
                item-text="text"
                item-value="value"
                :label="$t('finance.employees.department')"
                hide-details
                class="fin-field"
              ></v-select>
              <v-select
                v-model="filters.is_active"
                :items="statusOptions"
                item-text="text"
                item-value="value"
                :label="$t('finance.common.status')"
                hide-details
                class="fin-field"
              ></v-select>
              <v-btn small color="primary" @click="loadEmployees">{{ $t("finance.common.apply") }}</v-btn>
            </div>
          </div>

          <v-data-table
            class="fin-dt"
            :headers="employeeHeaders"
            :items="employees"
            :rows-per-page-items="[10, 25, 50]"
            :no-data-text="$t('finance.employees.noEmployees')"
          >
            <template v-slot:items="props">
              <tr
                :class="{ 'emp-row--selected': selectedEmployee && selectedEmployee.id === props.item.id }"
                @click="selectEmployee(props.item)"
              >
                <td class="fin-code">{{ props.item.employee_no || "-" }}</td>
                <td>{{ props.item.full_name }}</td>
                <td>{{ props.item.department || "-" }}</td>
                <td class="fin-num">{{ formatMoney(props.item.basic_salary) }}</td>
                <td>
                  <v-chip small :color="props.item.is_active ? 'success' : 'grey'" text-color="white">
                    {{ props.item.is_active ? $t("finance.employees.active") : $t("finance.employees.inactive") }}
                  </v-chip>
                </td>
                <td>
                  <div class="emp-actions">
                    <v-btn v-if="canWrite" small flat icon @click.stop="openEdit(props.item)">
                      <v-icon small>edit</v-icon>
                    </v-btn>
                    <v-btn v-if="canWrite" small flat icon color="error" @click.stop="openDelete(props.item)">
                      <v-icon small>archive</v-icon>
                    </v-btn>
                  </div>
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card>
      </v-flex>

      <!-- The payroll sheet is a panel here rather than a page of its own: a
           component has no meaning apart from the employee and the month it
           belongs to, so there is no state worth reaching without both. -->
      <v-flex xs12 md7 class="pl-1">
        <v-card>
          <div class="fin-header">
            <div class="fin-toolbar">
              <span class="fin-title">
                {{
                  selectedEmployee
                    ? $t("finance.employees.payrollFor", { name: selectedEmployee.full_name })
                    : $t("finance.employees.payrollTitle")
                }}
              </span>
              <v-spacer></v-spacer>
              <v-btn
                v-if="canWrite && selectedEmployee"
                small
                flat
                :loading="seeding"
                @click="seedFromContract"
              >
                {{ $t("finance.employees.seedFromContract") }}
              </v-btn>
              <v-btn v-if="canWrite && selectedEmployee" small color="primary" :loading="savingMonth" @click="saveMonth">
                {{ $t("finance.employees.saveMonth") }}
              </v-btn>
            </div>
            <div class="fin-filters">
              <v-text-field
                v-model="payrollMonth"
                type="month"
                :label="$t('finance.employees.month')"
                hide-details
                class="fin-field"
                @change="loadPayroll"
              ></v-text-field>
              <v-btn v-if="canWrite && selectedEmployee" small flat @click="addRow">
                <v-icon small left>add</v-icon>
                {{ $t("finance.employees.addRow") }}
              </v-btn>
            </div>
          </div>

          <div v-if="!selectedEmployee" class="emp-empty">
            {{ $t("finance.employees.selectEmployee") }}
          </div>

          <template v-else>
            <div class="fin-summary">
              <div class="fin-stat">
                <span class="fin-stat-label">{{ $t("finance.employees.gross") }}</span>
                <span class="fin-stat-value">{{ formatMoney(grossTotal) }}</span>
              </div>
              <div class="fin-stat">
                <span class="fin-stat-label">{{ $t("finance.employees.deductionsTotal") }}</span>
                <span class="fin-stat-value">{{ formatMoney(deductionTotal) }}</span>
              </div>
              <div class="fin-stat">
                <span class="fin-stat-label">{{ $t("finance.employees.net") }}</span>
                <span class="fin-stat-value">{{ formatMoney(netTotal) }}</span>
              </div>
            </div>

            <div class="emp-hints">
              <div>{{ $t("finance.employees.replaceHint") }}</div>
              <div>{{ $t("finance.employees.seedHint") }}</div>
              <div>{{ $t("finance.employees.lockedHint") }}</div>
            </div>

            <div v-if="!rows.length" class="emp-empty">{{ $t("finance.employees.noComponents") }}</div>

            <v-layout v-for="(row, index) in rows" :key="row.key" wrap align-center class="emp-row">
              <v-flex xs6 md2 class="pa-1">
                <v-select
                  v-model="row.kind"
                  :items="kindOptions"
                  item-text="text"
                  item-value="value"
                  :label="$t('finance.employees.kind')"
                  hide-details
                  :disabled="!canWrite"
                  @change="onKindChange(row)"
                ></v-select>
              </v-flex>
              <v-flex xs6 md3 class="pa-1">
                <v-select
                  v-model="row.component"
                  :items="componentOptionsFor(row.kind)"
                  item-text="text"
                  item-value="value"
                  :label="$t('finance.employees.component')"
                  hide-details
                  :disabled="!canWrite"
                ></v-select>
              </v-flex>
              <v-flex xs6 md2 class="pa-1">
                <v-text-field
                  v-model.number="row.amount"
                  type="number"
                  min="0"
                  :label="$t('finance.employees.amount')"
                  hide-details
                  :disabled="!canWrite"
                ></v-text-field>
              </v-flex>
              <v-flex xs6 md4 class="pa-1">
                <v-text-field
                  v-model="row.memo"
                  :label="$t('finance.employees.memo')"
                  hide-details
                  :disabled="!canWrite"
                ></v-text-field>
              </v-flex>
              <v-flex xs12 md1 class="pa-1">
                <v-btn v-if="canWrite" small flat icon color="error" @click="removeRow(row, index)">
                  <v-icon small>delete</v-icon>
                </v-btn>
              </v-flex>
            </v-layout>
          </template>
        </v-card>
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
import FinanceSectionNav from "@/components/finance/FinanceSectionNav";
import LoadingComponent from "@/components/LoadingComponent";
import { companyToday } from "@/util/companyTime";

// The vocabulary the server validates against. Kept in the same order and the
// same two sides, because a component accepted by one and bucketed by the
// other is exactly how a payroll total silently stops adding up.
const EARNING_COMPONENTS = ["basic", "allowance", "bonus", "overtime", "other"];
const DEDUCTION_COMPONENTS = ["insurance", "tax", "advance", "fine", "other"];

export default {
  name: "Employees",
  title() {
    return this.$t("finance.employees.pageTitle");
  },
  components: { FinanceSectionNav, LoadingComponent },
  data() {
    // Final-version doc 2026-10-05: the company (Damascus) month on the
    // server-synced clock, not the PC calendar ("YYYY-MM").
    const month = companyToday("YYYY-MM");

    return {
      loading: false,
      saving: false,
      savingMonth: false,
      seeding: false,
      deleting: false,
      formDialog: false,
      deleteDialog: false,
      deleteTarget: null,
      employees: [],
      departments: [],
      selectedEmployee: null,
      payrollMonth: month,
      rows: [],
      rowSeq: 0,
      filters: {
        search: null,
        department: null,
        is_active: null,
      },
      form: this.emptyForm(),
    };
  },
  computed: {
    // Reading a payroll report and maintaining the people on it are different
    // permissions; the page opens on the read one and hides every write.
    canWrite() {
      return this.hasPermission("MANAGE_FINANCE_JOURNALS");
    },
    isEditing() {
      return !!this.form.id;
    },
    employeeHeaders() {
      return [
        { text: this.$t("finance.employees.employeeNo"), value: "employee_no" },
        { text: this.$t("finance.employees.fullName"), value: "full_name" },
        { text: this.$t("finance.employees.department"), value: "department" },
        { text: this.$t("finance.employees.basicSalary"), value: "basic_salary", align: "end" },
        { text: this.$t("finance.common.status"), value: "is_active" },
        { text: this.$t("finance.common.actions"), value: "actions", sortable: false },
      ];
    },
    departmentOptions() {
      return [{ text: this.$t("finance.employees.allDepartments"), value: null }].concat(
        this.departments.map(function (department) {
          return { text: department, value: department };
        })
      );
    },
    statusOptions() {
      return [
        { text: this.$t("finance.employees.allStatuses"), value: null },
        { text: this.$t("finance.employees.active"), value: 1 },
        { text: this.$t("finance.employees.inactive"), value: 0 },
      ];
    },
    kindOptions() {
      return [
        { text: this.$t("finance.employees.earning"), value: "EARNING" },
        { text: this.$t("finance.employees.deduction"), value: "DEDUCTION" },
      ];
    },
    // Totals are summed from the SHEET, never from a stored net: the server
    // stores no net at all, precisely so the two can never disagree.
    grossTotal() {
      return this.sumRows("EARNING");
    },
    deductionTotal() {
      return this.sumRows("DEDUCTION");
    },
    netTotal() {
      return this.grossTotal - this.deductionTotal;
    },
  },
  mounted() {
    this.loadEmployees();
    this.loadDepartments();
  },
  methods: {
    emptyForm() {
      return {
        id: null,
        employee_no: null,
        full_name: null,
        department: null,
        job_title: null,
        phone_number: null,
        hired_at: null,
        terminated_at: null,
        basic_salary: null,
        is_active: true,
      };
    },
    getErrorMessage(error, fallback) {
      if (error && error.response && error.response.data && error.response.data.message) {
        return error.response.data.message;
      }

      return fallback;
    },
    componentLabel(value) {
      var key = "finance.employees.components." + value;
      var translated = this.$t(key);

      return translated === key ? value : translated;
    },
    componentOptionsFor(kind) {
      var names = kind === "DEDUCTION" ? DEDUCTION_COMPONENTS : EARNING_COMPONENTS;

      return names.map(function (name) {
        return { text: this.componentLabel(name), value: name };
      }, this);
    },
    sumRows(kind) {
      return this.rows
        .filter(function (row) {
          return row.kind === kind;
        })
        .reduce(
          function (sum, row) {
            return sum + (this.toStoredAmount(row.amount) || 0);
          }.bind(this),
          0
        );
    },
    loadEmployees() {
      this.loading = true;
      this.axios
        .get(this.api.ACTIONS.ACCOUNTING_EMPLOYEES, {
          params: {
            search: this.filters.search,
            department: this.filters.department,
            is_active: this.filters.is_active,
            perPage: 200,
          },
        })
        .then(
          function (response) {
            var page = (response.data && response.data.data) || {};
            this.employees = page.data || [];
            // A selected employee who fell out of the filtered list keeps his
            // sheet open; dropping the panel would look like data loss.
            if (this.selectedEmployee) {
              var stillListed = this.employees.filter(
                function (employee) {
                  return employee.id === this.selectedEmployee.id;
                }.bind(this)
              );
              if (stillListed.length) {
                this.selectedEmployee = stillListed[0];
              }
            }
          }.bind(this)
        )
        .catch(
          function (error) {
            window.getApp.$emit(
              "SHOW_TOAST",
              this.getErrorMessage(error, this.$t("finance.employees.failedLoad")),
              "red"
            );
          }.bind(this)
        )
        .finally(
          function () {
            this.loading = false;
          }.bind(this)
        );
    },
    loadDepartments() {
      this.axios
        .get(this.api.ACTIONS.ACCOUNTING_EMPLOYEE_DEPARTMENTS)
        .then(
          function (response) {
            this.departments = (response.data && response.data.data) || [];
          }.bind(this)
        )
        .catch(function () {});
    },
    openCreate() {
      this.form = this.emptyForm();
      this.formDialog = true;
    },
    openEdit(employee) {
      this.form = {
        id: employee.id,
        employee_no: employee.employee_no,
        full_name: employee.full_name,
        department: employee.department,
        job_title: employee.job_title,
        phone_number: employee.phone_number,
        hired_at: employee.hired_at ? String(employee.hired_at).substring(0, 10) : null,
        terminated_at: employee.terminated_at ? String(employee.terminated_at).substring(0, 10) : null,
        // Stored in old-unit SYP; the input edits NEW units.
        basic_salary:
          employee.basic_salary === null || employee.basic_salary === undefined
            ? null
            : Number(employee.basic_salary) / 100,
        is_active: employee.is_active !== false,
      };
      this.formDialog = true;
    },
    closeForm() {
      this.formDialog = false;
      this.form = this.emptyForm();
    },
    submitForm() {
      if (!this.form.full_name || !String(this.form.full_name).trim()) {
        window.getApp.$emit("SHOW_TOAST", this.$t("finance.employees.nameRequired"), "red");
        return;
      }

      var payload = {
        employee_no: this.form.employee_no || null,
        full_name: this.form.full_name,
        department: this.form.department || null,
        job_title: this.form.job_title || null,
        phone_number: this.form.phone_number || null,
        hired_at: this.form.hired_at || null,
        terminated_at: this.form.terminated_at || null,
        basic_salary: this.toStoredAmount(this.form.basic_salary),
        is_active: this.form.is_active ? 1 : 0,
      };

      this.saving = true;
      var request = this.isEditing
        ? this.axios.put(this.api.ACTIONS.ACCOUNTING_EMPLOYEE(this.form.id), payload)
        : this.axios.post(this.api.ACTIONS.ACCOUNTING_EMPLOYEES, payload);

      request
        .then(
          function () {
            window.getApp.$emit("SHOW_TOAST", this.$t("finance.employees.saved"), "green");
            this.closeForm();
            this.loadEmployees();
            this.loadDepartments();
          }.bind(this)
        )
        .catch(
          function (error) {
            window.getApp.$emit(
              "SHOW_TOAST",
              this.getErrorMessage(error, this.$t("finance.employees.failedSave")),
              "red"
            );
          }.bind(this)
        )
        .finally(
          function () {
            this.saving = false;
          }.bind(this)
        );
    },
    openDelete(employee) {
      this.deleteTarget = employee;
      this.deleteDialog = true;
    },
    submitDelete() {
      if (!this.deleteTarget) {
        return;
      }

      this.deleting = true;
      this.axios
        .delete(this.api.ACTIONS.ACCOUNTING_EMPLOYEE(this.deleteTarget.id))
        .then(
          function () {
            window.getApp.$emit("SHOW_TOAST", this.$t("finance.employees.deleted"), "green");
            if (this.selectedEmployee && this.selectedEmployee.id === this.deleteTarget.id) {
              this.selectedEmployee = null;
              this.rows = [];
            }
            this.deleteDialog = false;
            this.deleteTarget = null;
            this.loadEmployees();
          }.bind(this)
        )
        .catch(
          function (error) {
            window.getApp.$emit(
              "SHOW_TOAST",
              this.getErrorMessage(error, this.$t("finance.employees.failedDelete")),
              "red"
            );
          }.bind(this)
        )
        .finally(
          function () {
            this.deleting = false;
          }.bind(this)
        );
    },
    selectEmployee(employee) {
      this.selectedEmployee = employee;
      this.loadPayroll();
    },
    loadPayroll() {
      if (!this.selectedEmployee || !this.payrollMonth) {
        return;
      }

      this.loading = true;
      this.axios
        .get(this.api.ACTIONS.ACCOUNTING_PAYROLL_COMPONENTS, {
          params: {
            employee_id: this.selectedEmployee.id,
            month: this.payrollMonth,
            perPage: 300,
          },
        })
        .then(
          function (response) {
            var page = (response.data && response.data.data) || {};
            this.rows = (page.data || []).map(
              function (component) {
                return this.toRow(component);
              }.bind(this)
            );
          }.bind(this)
        )
        .catch(
          function (error) {
            window.getApp.$emit(
              "SHOW_TOAST",
              this.getErrorMessage(error, this.$t("finance.employees.failedLoadPayroll")),
              "red"
            );
          }.bind(this)
        )
        .finally(
          function () {
            this.loading = false;
          }.bind(this)
        );
    },
    // `key` exists so v-for is keyed on something stable across a save that
    // replaces the month and therefore hands back brand-new ids.
    toRow(component) {
      this.rowSeq += 1;

      return {
        key: "row-" + this.rowSeq,
        id: component ? component.id : null,
        kind: component ? component.kind : "EARNING",
        component: component ? component.component : null,
        amount:
          component && component.amount !== null && component.amount !== undefined
            ? Number(component.amount) / 100
            : null,
        memo: component ? component.memo : null,
      };
    },
    addRow() {
      this.rows.push(this.toRow(null));
    },
    onKindChange(row) {
      // An EARNING called 'insurance' would be added to gross pay, so a side
      // change drops a component that does not belong to the new side.
      var allowed = row.kind === "DEDUCTION" ? DEDUCTION_COMPONENTS : EARNING_COMPONENTS;
      if (allowed.indexOf(row.component) === -1) {
        row.component = null;
      }
    },
    removeRow(row, index) {
      // A row that never reached the server is only a local edit; a persisted
      // one is deleted through its own endpoint, so removing the LAST line of
      // a month is possible at all (a bulk save requires at least one line).
      if (!row.id) {
        this.rows.splice(index, 1);
        return;
      }

      this.axios
        .delete(this.api.ACTIONS.ACCOUNTING_PAYROLL_COMPONENT(row.id))
        .then(
          function () {
            window.getApp.$emit("SHOW_TOAST", this.$t("finance.employees.componentDeleted"), "green");
            this.loadPayroll();
          }.bind(this)
        )
        .catch(
          function (error) {
            window.getApp.$emit(
              "SHOW_TOAST",
              this.getErrorMessage(error, this.$t("finance.employees.failedDeleteComponent")),
              "red"
            );
          }.bind(this)
        );
    },
    saveMonth() {
      if (!this.selectedEmployee || !this.payrollMonth) {
        return;
      }

      if (!this.rows.length) {
        // A bulk save needs at least one line, so an empty sheet is not how a
        // month is cleared; the row delete is.
        window.getApp.$emit("SHOW_TOAST", this.$t("finance.employees.noComponents"), "orange");
        return;
      }

      var invalid = this.rows.filter(function (row) {
        return !row.component || row.amount === null || row.amount === "" || Number(row.amount) < 0;
      });
      if (invalid.length) {
        window.getApp.$emit("SHOW_TOAST", this.$t("finance.employees.amountRequired"), "red");
        return;
      }

      this.savingMonth = true;
      this.axios
        .post(this.api.ACTIONS.ACCOUNTING_PAYROLL_COMPONENTS_BULK, {
          employee_id: this.selectedEmployee.id,
          month: this.payrollMonth,
          // Rewrites the month rather than appending, so a corrected line does
          // not leave the original behind to be summed twice.
          replace: 1,
          components: this.rows.map(
            function (row) {
              return {
                kind: row.kind,
                component: row.component,
                amount: this.toStoredAmount(row.amount),
                memo: row.memo || null,
              };
            }.bind(this)
          ),
        })
        .then(
          function () {
            window.getApp.$emit("SHOW_TOAST", this.$t("finance.employees.monthSaved"), "green");
            this.loadPayroll();
          }.bind(this)
        )
        .catch(
          function (error) {
            window.getApp.$emit(
              "SHOW_TOAST",
              this.getErrorMessage(error, this.$t("finance.employees.failedSaveMonth")),
              "red"
            );
          }.bind(this)
        )
        .finally(
          function () {
            this.savingMonth = false;
          }.bind(this)
        );
    },
    seedFromContract() {
      if (!this.selectedEmployee || !this.payrollMonth) {
        return;
      }

      this.seeding = true;
      this.axios
        .post(this.api.ACTIONS.ACCOUNTING_PAYROLL_COMPONENTS_SEED, {
          month: this.payrollMonth,
          employee_id: this.selectedEmployee.id,
        })
        .then(
          function (response) {
            var result = (response.data && response.data.data) || {};
            window.getApp.$emit(
              "SHOW_TOAST",
              this.$t("finance.employees.seedDone", {
                created: result.components_created || 0,
                considered: result.employees_considered || 0,
              }),
              "green"
            );
            this.loadPayroll();
          }.bind(this)
        )
        .catch(
          function (error) {
            window.getApp.$emit(
              "SHOW_TOAST",
              this.getErrorMessage(error, this.$t("finance.employees.failedSeed")),
              "red"
            );
          }.bind(this)
        )
        .finally(
          function () {
            this.seeding = false;
          }.bind(this)
        );
    },
  },
};
</script>

<style scoped>
.emp-notice {
  font-size: 13px;
  line-height: 1.6;
  margin-bottom: 10px;
}
.emp-notice--warn {
  padding: 8px 12px;
  border-radius: 4px;
  background: #fff8e1;
  border-inline-start: 4px solid #f9a825;
  font-weight: 600;
}
.emp-empty {
  padding: 24px 12px;
  text-align: center;
  color: rgba(0, 0, 0, 0.55);
  font-size: 13px;
}
.emp-hints {
  padding: 0 12px 8px;
  font-size: 12px;
  line-height: 1.6;
  color: rgba(0, 0, 0, 0.6);
}
.emp-row {
  border-top: 1px solid #eeeeee;
  padding: 0 8px;
}
/* final-version doc 2026-10-05 review: paint the selected row on its cells,
   not the tr. The global fin-dt zebra (tr:nth-child(even)) and hover
   (tr:hover !important) rules in finance-compact.css outrank a scoped tr
   background, so even rows lost the highlight. A td background sits above
   the tr background, the same pattern the report pages use. */
.emp-row--selected td {
  background: #e3f2fd;
}
.emp-row--selected:hover td {
  background: #d4e9fb;
}
.emp-actions {
  display: flex;
  align-items: center;
}
</style>
